import React, { useState } from "react";
import { Grid, Typography } from "@mui/material";
import { PhoneInputField } from "../../components/Form";
import { useLocation } from "react-router";
import { useFormik } from "formik";
import { useI18n } from "../../providers/I18n/I18n";
import useNotification from "../../components/Notification/useNotification";
import {
  OptOutMobileImage,
  RMI_LOGO,
  TickCircleIcon,
} from "../../providers/RexIconsSVG/RexIconsSVG";
import Widget from "../../components/Widget/Widget";
import { Button } from "../../components/Wrappers/Wrappers";
import { distributionListModule } from "../../providers/RakutenSDK/RakutenSDK";
import { campaignOptOutSchema } from "../../dataForms/EntityDataForms/validations";
import ConfirmModal from "../../components/ConfirmModal";
import useStyles from "./styles";

const CampaignOptOut = () => {
  const classes = useStyles();
  const { t } = useI18n();
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isOptOutSuccess, setIsOptOutSuccess] = useState(false);
  const location = useLocation();
  const data = new URLSearchParams(location.search);
  const [error, setError] = useState<Error | string | undefined>();
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const ConfirmBody = () => {
    return (
      <>
        <h2>{t("confirm_opt_out")}</h2>
        <span>{t("stop_receiving_msg")}</span>
      </>
    );
  };

  const formik = useFormik<{
    phone: string;
  }>({
    initialValues: {
      phone: data.get("phone-number") || "",
    },
    validationSchema: campaignOptOutSchema,
    onSubmit: async (values: { phone?: string }) => {
      try {
        setIsLoading(true);
        await distributionListModule.optOut(
          data.get("enterprise-id") || "",
          data.get("distribution-list-id") || "",
          [values?.phone],
          true,
        );
        setIsLoading(false);
        showNotification("success", t("opt_out_success"));
        setIsOptOutSuccess(true);
        setShowConfirmModal(false);
      } catch (err) {
        setIsLoading(false);
        setError(err?.message);
        setIsOptOutSuccess(false);
      }
    },
  });

  const handleProceedClick = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      setShowConfirmModal(true);
    } else {
      formik.setTouched({ phone: true });
    }
  };

  return (
    <Grid container className={classes.container}>
      <div className={classes.formContainer}>
        <ConfirmModal
          isLoading={isLoading}
          show={showConfirmModal}
          onCancel={() => setShowConfirmModal(false)}
          bodyImage={
            window.innerWidth < 500 ? (
              <TickCircleIcon viewBox="1.5 -3 3 3" />
            ) : (
              <TickCircleIcon viewBox="-1.5 -3 3 3" />
            )
          }
          BodyComponent={() => ConfirmBody()}
          confirmedText={t("yes_opt_out")}
          cancelText={t("cancel")}
          onConfirmed={formik.handleSubmit}
        />
        <RMI_LOGO className={classes.logotypeFormImage} viewBox="0 0 379 84" />
        <Widget customWidgetWrapperClass={classes.customWrapper}>
          {isOptOutSuccess ? (
            <>
              <OptOutMobileImage
                className={classes.optOutImage}
                viewBox="0 0 379 84"
              />
              <div className={classes.greeting}>{t("Opt_out_success_header")}</div>
              <Typography variant="caption">
                {t("opt_out_success_subtext")}
              </Typography>
            </>
          ) : (
            <>
              <div className={classes.greeting}>{t("Opt_out_of_campaign")}</div>
              <Typography variant="caption">{t("opt_out_helper")}</Typography>
              {error && <div className={classes.error}>{t(error)}</div>}
              <form onSubmit={formik.handleSubmit}>
                <div className={classes.form}>
                  <PhoneInputField
                    id="phone"
                    value={formik.values.phone}
                    errorMessage={
                      formik.errors["phone"] && formik.touched["phone"] 
                        ? t(formik.errors["phone"])
                        : undefined 
                    }
                    isTouched={formik.touched["phone"]}
                    onChange={formik.handleChange("phone")}
                    required={true}
                    label={t("phone_number")}
                  />
                </div>
                <Grid className={classes.formButton}>
                  <Button
                    variant="contained"
                    isLoading={isLoading}
                    type="submit" 
                    fullWidth
                    onClick={handleProceedClick}
                  >
                    <span className={classes.containerButtonText}>
                      {t("proceed")}
                    </span>
                  </Button>
                </Grid>
              </form>
            </>
          )}
        </Widget>
      </div>
    </Grid>
  );
};

export default CampaignOptOut;
